import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CatalogStatus } from '../../../shared/types/models.js'
import { availableLifecycleActions, type LifecycleAction } from '../../../shared/utils/catalog.js'

interface Props {
  status: CatalogStatus
  entityName: string
  disabled: boolean
  onAction: (action: LifecycleAction) => Promise<void>
}

const ACTION_LABEL: Record<LifecycleAction, string> = {
  suspend: 'menu.suspend',
  activate: 'menu.activate',
  archive: 'menu.archive',
}

const ACTION_CLASS: Record<LifecycleAction, string> = {
  suspend: 'btn btn-ghost',
  activate: 'btn btn-ghost',
  archive: 'btn btn-danger',
}

export default function LifecycleActions({ status, entityName, disabled, onAction }: Props) {
  const { t } = useTranslation()
  const [pending, setPending] = useState<LifecycleAction | null>(null)

  const actions = availableLifecycleActions(status)

  async function run(action: LifecycleAction) {
    if (action === 'archive') {
      const ok = window.confirm(t('menu.archiveConfirm', { name: entityName }))
      if (!ok) return
    }
    setPending(action)
    try {
      await onAction(action)
    } finally {
      setPending(null)
    }
  }

  if (disabled || actions.length === 0) return null

  return (
    <div className="lifecycle-actions">
      {actions.map(action => (
        <button
          key={action}
          type="button"
          className={ACTION_CLASS[action]}
          disabled={pending !== null}
          onClick={() => void run(action)}
        >
          {pending === action ? t('menu.processing') : t(ACTION_LABEL[action])}
        </button>
      ))}
    </div>
  )
}